import { externalReferrerHost, getAttribution } from "./attribution";
import { readConsentStatus } from "./consent";
import { detectDevice } from "./device";
import { currentPath, getAnalyticsLocale } from "./page";

/**
 * Consent-free audience counter (CNIL audience-measurement exemption): aggregate hits with no
 * identifier, no cookie and no storage. Shipped off; `NEXT_PUBLIC_ANON_AUDIENCE=1` turns it on.
 */
const ENABLED = process.env.NEXT_PUBLIC_ANON_AUDIENCE === "1";

/**
 * localStorage key recording that the visitor objected to the counter. Storing the objection is
 * strictly necessary to honour it, so it needs no consent.
 */
const OPT_OUT_KEY = "flots-audience-optout";

const optOutListeners = new Set<() => void>();

// Views already counted in this document. Module memory only: the first view of a document
// is the visit's entry page unless the visitor came from another page of the site.
let viewsInDocument = 0;

/** Whether the browser sends a do-not-track signal (Global Privacy Control or Do Not Track). */
export function hasBrowserPrivacySignal(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true || navigator.doNotTrack === "1";
}

/**
 * Whether this visitor objected to the anonymous counter, explicitly (privacy page) or through
 * a browser privacy signal. The CNIL exemption requires that objection to be possible.
 */
export function isAudienceOptedOut(): boolean {
  if (hasBrowserPrivacySignal()) return true;
  try {
    return localStorage.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Records (or withdraws) the visitor's objection to the anonymous counter.
 *
 * @param optedOut - True to stop being counted on this browser.
 */
export function setAudienceOptOut(optedOut: boolean): void {
  try {
    if (optedOut) localStorage.setItem(OPT_OUT_KEY, "1");
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    // Storage blocked: the objection cannot be remembered beyond this page.
  }
  optOutListeners.forEach((listener) => listener());
}

/**
 * Subscribes to objection changes, for `useSyncExternalStore`.
 *
 * @param listener - Called after every change.
 * @returns The unsubscribe function.
 */
export function subscribeAudienceOptOut(listener: () => void): () => void {
  optOutListeners.add(listener);
  return () => optOutListeners.delete(listener);
}

function send(payload: Record<string, string | boolean>): void {
  if (!ENABLED || typeof navigator === "undefined") return;
  if (navigator.webdriver) return;
  if (isAudienceOptedOut()) return;
  try {
    navigator.sendBeacon?.("/api/obs/hit", JSON.stringify(payload));
  } catch {
    // Counting must never break the page.
  }
}

/**
 * Counts one anonymous page view. Call on every route change, first page included.
 * Does nothing unless the counter is enabled.
 */
export function sendAudienceView(): void {
  if (!ENABLED || typeof window === "undefined") return;
  const first = viewsInDocument === 0;
  viewsInDocument += 1;
  // Entry page: first view of this document and no referrer from the site itself.
  const entry = first && (!document.referrer || externalReferrerHost() !== "");
  const attribution = getAttribution();
  send({
    kind: "view",
    path: currentPath(),
    locale: getAnalyticsLocale(),
    source: attribution?.source ?? "",
    medium: attribution?.medium ?? "",
    campaign: attribution?.campaign ?? "",
    referrerHost: attribution?.referrerHost ?? "",
    device: detectDevice(),
    entry,
    consent: readConsentStatus(),
  });
}

/**
 * Counts one cookie-banner choice, which gives the consent rate. Does nothing unless the
 * counter is enabled.
 *
 * @param choice - The button the visitor pressed.
 */
export function sendAudienceConsent(choice: "accepted" | "rejected"): void {
  if (!ENABLED) return;
  send({
    kind: choice === "accepted" ? "consent-accepted" : "consent-rejected",
    path: currentPath(),
    locale: getAnalyticsLocale(),
    device: detectDevice(),
    consent: choice,
  });
}
